/**
 * Market-open sounds, synthesized with the Web Audio API — no audio assets,
 * no broadcast-recording rights questions. Browsers only allow sound after a
 * user gesture, so the context is created when the user arms the bell via the
 * header toggle.
 */

export type BellSound = 'real' | 'nyse' | 'gong' | 'boxing' | 'chime' | 'ship';

export const BELL_SOUNDS: Array<{ key: BellSound; label: string }> = [
  { key: 'real', label: 'NYSE Bell (recording)' },
  { key: 'nyse', label: 'NYSE Bell (synth)' },
  { key: 'gong', label: 'Deep Gong' },
  { key: 'boxing', label: 'Boxing Bell' },
  { key: 'chime', label: 'Soft Chime' },
  { key: 'ship', label: "Ship's Bell" },
];

let ctx: AudioContext | null = null;

export function armAudio(): void {
  if (!ctx) {
    ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)() as AudioContext;
  }
  if (ctx.state === 'suspended') void ctx.resume();
}

function master(): GainNode | null {
  if (!ctx) return null;
  if (ctx.state === 'suspended') void ctx.resume();
  const g = ctx.createGain();
  g.gain.value = 0.5;
  g.connect(ctx.destination);
  return g;
}

/** One metallic strike: inharmonic partials with an exponential decay. */
function strike(
  at: number,
  gain: number,
  out: GainNode,
  partials: number[],
  decay: number
): void {
  if (!ctx) return;
  partials.forEach((freq, i) => {
    const osc = ctx!.createOscillator();
    const env = ctx!.createGain();
    osc.type = i < 2 ? 'triangle' : 'sine';
    osc.frequency.value = freq * (1 + (Math.random() - 0.5) * 0.004);
    const g = (gain * [1, 0.6, 0.35, 0.2, 0.1][Math.min(i, 4)]) / partials.length;
    env.gain.setValueAtTime(g, at);
    env.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    osc.connect(env).connect(out);
    osc.start(at);
    osc.stop(at + decay + 0.05);
  });
}

// The real NYSE bell recording (frontend/public/sounds), decoded once into the
// same armed AudioContext so the scheduled 9:30 ring plays as reliably as the
// synthesized sounds (no separate HTMLAudio autoplay gamble).
let realBuffer: AudioBuffer | null = null;
let realLoading: Promise<AudioBuffer | null> | null = null;

function loadRealBell(): Promise<AudioBuffer | null> {
  if (realBuffer) return Promise.resolve(realBuffer);
  if (!ctx) return Promise.resolve(null);
  if (!realLoading) {
    realLoading = fetch('/sounds/nyse-bell.mp3')
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((buf) => ctx!.decodeAudioData(buf))
      .then((decoded) => {
        realBuffer = decoded;
        return decoded;
      })
      .catch(() => {
        realLoading = null; // allow a retry on the next play attempt
        return null;
      });
  }
  return realLoading;
}

function playReal(out: GainNode, preview: boolean): void {
  void loadRealBell().then((buffer) => {
    if (!buffer || !ctx) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(out);
    const t0 = ctx.currentTime + 0.05;
    src.start(t0);
    if (preview) src.stop(t0 + 3); // short taste when arming; full 20s at 9:30
  });
}

const BRASS = [1046.5, 1318.5, 1567.98, 2093, 2637];
const GONG = [196, 294, 392, 523, 740];
const SHIP = [784, 988, 1319, 1976];

/** Play the chosen sound. preview = shortened arm-confirmation version. */
export function playBell(sound: BellSound, preview = false): void {
  const out = master();
  if (!ctx || !out) return;
  const t0 = ctx.currentTime + 0.05;

  switch (sound) {
    case 'real': {
      playReal(out, preview);
      break;
    }
    case 'nyse': {
      // rapid continuous brass ring, ~8s with a tail-off (3 strikes in preview)
      const strikes = preview ? 3 : 72;
      for (let i = 0; i < strikes; i++) {
        const fade = preview ? 1 : Math.min(1, (strikes - i) / 18);
        strike(t0 + i * 0.11, 0.9 * fade, out, BRASS, 0.4);
      }
      break;
    }
    case 'gong': {
      // one deep resonant hit, long decay (second hit in the full version)
      strike(t0, 1.6, out, GONG, 3.5);
      if (!preview) strike(t0 + 2.2, 1.2, out, GONG, 3.5);
      break;
    }
    case 'boxing': {
      // classic round-start: three spaced bright dings
      const hits = preview ? 2 : 3;
      for (let i = 0; i < hits; i++) strike(t0 + i * 0.55, 1.3, out, BRASS, 0.7);
      break;
    }
    case 'chime': {
      // gentle ascending C–E–G, soft sine voices
      const notes = [523.25, 659.25, 783.99];
      notes.slice(0, preview ? 2 : 3).forEach((f, i) => {
        strike(t0 + i * 0.3, 0.9, out, [f, f * 2, f * 3], 1.4);
      });
      break;
    }
    case 'ship': {
      // paired ding-ding strokes, nautical style (one pair in preview)
      const pairs = preview ? 1 : 2;
      for (let p = 0; p < pairs; p++) {
        const base = t0 + p * 1.1;
        strike(base, 1.1, out, SHIP, 0.9);
        strike(base + 0.28, 1.1, out, SHIP, 0.9);
      }
      break;
    }
  }
}
